// Duplicate Keywords — Keywords section, deterministic check.
// Renders each duplicate-keyword group as a section with the colliding instances listed —
// auditor can see which ad groups + campaigns hold copies of each shared text.

import type { EvidenceProps } from './registry';

type KeywordMatchType = 'EXACT' | 'PHRASE' | 'BROAD' | 'UNSPECIFIED' | 'UNKNOWN';

interface KeywordInstance {
  text: string;
  matchType?: KeywordMatchType;
  adGroupId?: string;
  adGroupName?: string;
  campaignId?: string;
  campaignName?: string;
}

interface DuplicateGroup {
  normalizedText: string;
  displayText: string;
  campaignId?: string;
  campaignName?: string;
  count: number;
  matchTypes: KeywordMatchType[];
  instances: KeywordInstance[];
}

interface DuplicateKeywordsShape {
  totalKeywords: number;
  duplicateGroupCount: number;
  duplicateInstanceCount: number;
  duplicateShare: number;
  groups: DuplicateGroup[];
}

const MATCH_TYPE_LABEL: Record<string, string> = {
  EXACT: '[exact]',
  PHRASE: '"phrase"',
  BROAD: 'broad'
};

function matchTypeLabel(m: string | undefined): string {
  if (!m) return '';
  return MATCH_TYPE_LABEL[m] ?? m;
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

export const DuplicateKeywordsEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as DuplicateKeywordsShape;

  if (e.duplicateGroupCount === 0) {
    return (
      <div className='evidence'>
        <p className='evidence__lead'>
          No within-campaign duplicate keywords found across {e.totalKeywords} enabled keywords.
        </p>
      </div>
    );
  }

  return (
    <div className='evidence'>
      <p className='evidence__lead'>
        <strong>{e.duplicateGroupCount}</strong> within-campaign duplicate group
        {e.duplicateGroupCount === 1 ? '' : 's'} found ({e.duplicateInstanceCount} total instances;{' '}
        {pct(e.duplicateShare)} of all enabled keywords). Within-campaign duplicates create internal
        auction conflicts — Google picks the highest-QS instance and the rest do nothing.
        Consolidate each into a single ad group.
      </p>

      {e.groups.slice(0, 25).map((g, gi) => (
        <section key={`${g.campaignId}-${g.normalizedText}-${gi}`} className='evidence__section'>
          <h4 className='evidence__section-title'>
            <span className='evidence__group-name'>{g.displayText}</span>
            <span className='evidence__section-tag evidence__section-tag--context'>
              {g.count} instances in {g.campaignName ?? 'campaign'}
            </span>
            {g.matchTypes.length > 1 && (
              <span className='evidence__section-tag evidence__section-tag--actionable'>
                Mixed match types
              </span>
            )}
          </h4>
          <table className='evidence__table evidence__table--duplicate-keywords'>
            <thead>
              <tr>
                <th>Ad group</th>
                <th>Match type</th>
              </tr>
            </thead>
            <tbody>
              {g.instances.map((i, idx) => (
                <tr key={`${i.adGroupId ?? i.adGroupName}-${idx}`}>
                  <td>{i.adGroupName ?? '(unnamed)'}</td>
                  <td>{matchTypeLabel(i.matchType)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}

      {e.groups.length > 25 && (
        <p className='evidence__footnote'>
          Showing the top 25 duplicate groups by instance count. {e.groups.length - 25} more
          duplicate groups exist — query the raw evidence for the full list.
        </p>
      )}
    </div>
  );
};
